import type { QuestDef } from '../../game/quests';
import { MAIN_QUESTS, WORLD_QUESTS } from './main';
import { SIDE_QUESTS } from './side';

export { MAIN_QUESTS, WORLD_QUESTS } from './main';
export { SIDE_QUESTS } from './side';
export { NPC_AT } from './kit';

/**
 * Every story, side and world quest, in registration order (main first, so an
 * NPC who offers both shows the main quest's lines).
 */
export const ALL_QUESTS: QuestDef[] = [...MAIN_QUESTS, ...SIDE_QUESTS, ...WORLD_QUESTS];

/** Chapters of the main story, for the quest journal. */
export interface StoryChapter {
  id: string;
  name: string;
  /** Short line under the chapter title. */
  blurb: string;
  quests: string[];
}

const range = (from: number, to: number): string[] =>
  Array.from({ length: to - from + 1 }, (_, i) => `mq_${String(from + i).padStart(3, '0')}`);

export const STORY_CHAPTERS: StoryChapter[] = [
  {
    id: 'prologue',
    name: 'Prologue: Ember-Touched',
    blurb: 'The Emberwild Outpost, after the Aetherfall',
    quests: range(1, 4),
  },
  {
    id: 'act1',
    name: 'Act I: The Ember Shard',
    blurb: 'Grublings, bandits and the Hollowroot Crypt',
    quests: range(5, 14),
  },
  {
    id: 'interlude',
    name: 'Interlude: The Lantern Watch',
    blurb: 'Havenreach, city of the Waystone Dais',
    quests: range(15, 18),
  },
  {
    id: 'act2',
    name: 'Act II: The Dune Shard',
    blurb: 'Sunscar, Haven Isle and the Sandsworn Tomb',
    quests: range(19, 32),
  },
  {
    id: 'act3',
    name: 'Act III: The Rime Shard',
    blurb: 'Frosthollow, the Hollow Bell and Frostfang Keep',
    quests: range(33, 42),
  },
  {
    id: 'epilogue',
    name: 'Epilogue: The First Flame',
    blurb: 'The Lantern rekindled; the road to the Hollow Throne',
    quests: range(43, 45),
  },
  {
    id: 'endgame',
    name: 'The Four Shards',
    blurb: 'Rift Delve, the Colossi, the Drowned Stair and the Warlord Siege',
    quests: WORLD_QUESTS.map((q) => q.id),
  },
];

/**
 * Collectible hints: a quest reward flag that reveals every remaining
 * Glimmerseed of a zone on the map (M).
 */
export const COLLECTIBLE_HINTS: { flag: string; zone: string; kind: 'seed' }[] = [
  { flag: 'hint_ew_seeds', zone: 'emberwild', kind: 'seed' },
  { flag: 'hint_hr_seeds', zone: 'havenreach', kind: 'seed' },
  { flag: 'hint_ss_seeds', zone: 'sunscar', kind: 'seed' },
  { flag: 'hint_fh_seeds', zone: 'frosthollow', kind: 'seed' },
  { flag: 'hint_gl_seeds', zone: 'glimmer', kind: 'seed' },
];

/**
 * Tutorial flags that the quests feature derives from game state (tempering,
 * sigils, cards, gems, bonds, bounties), so "do X once" objectives complete
 * even when the player did X before taking the quest.
 */
export const TUTORIAL_FLAGS: Record<string, string> = {
  tut_tempered: 'Tempered a piece of equipment',
  tut_sigil_read: 'Read a sigil codex',
  tut_card_slotted: 'Placed a relic card in the deck',
  tut_gem_socketed: 'Socketed a gem',
  tut_bond: 'Built a bond with an NPC',
  tut_bounty: 'Completed a bounty',
};

/**
 * Story flags set by quest rewards that other systems can gate on:
 * `act1_complete`, `act2_started`, `sailing_unlocked`, `haven_unlocked`,
 * `act2_tomb`, `act3_started`, `act3_keep`, `lantern_first_flame`,
 * `story_complete`, `unlock_rift`, `unlock_colossus`, `unlock_deepvault`,
 * `unlock_siege` and (endgame) `lantern_relit`.
 */
export const STORY_FLAGS = [
  'act1_crypt',
  'act1_complete',
  'act2_started',
  'sailing_unlocked',
  'haven_unlocked',
  'act2_tomb',
  'act3_started',
  'act3_keep',
  'lantern_first_flame',
  'story_complete',
  'unlock_rift',
  'unlock_colossus',
  'unlock_deepvault',
  'unlock_siege',
  'lantern_relit',
] as const;
